import { HttpContext } from '@angular/common/http';
import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import {
    catchError,
    distinctUntilChanged,
    EMPTY,
    expand,
    filter,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil,
    takeWhile,
    throwError
} from 'rxjs';
import { AiChatApi } from './ai-chat-api.abstract';
import {
    AiInitThreadDescriptor,
    AiPromptDescriptor,
    AiRequestDescriptor,
    AiResponseType,
    AiRestoreThreadDescriptor,
    AiSendFeedbackDescriptor,
    AiStreamDescriptor,
    AiSuggestionsDescriptor,
    InitThreadResponse,
    PollResult,
    PromptResponse,
    RawStreamResponse,
    RestoreThreadResponse,
    StreamResponse,
    SuggestionsResponse
} from './endpoint-contracts';
import { AiInterruptDescriptor, InterruptRequest, InterruptResponse } from './endpoint-contracts';
import { injectWidgetConfigProp } from '@panaya/ai-chat/utils';
import { AiChatRequestService } from './ai-chat-request.service';
import { AiChatInterceptorsService } from './interceptors';
import { UserFeedback } from '@panaya/ai-chat/shared';
import { AiRequestContext } from './endpoint-contracts/context';
import { AiChatRouteService } from '../services';
import { AI_REQUEST_CONTEXT_CONTRIBUTORS, AiRequestContextContributor } from './context';

declare const ngDevMode: unknown;

@Injectable({
    providedIn: 'root'
})
export class AiChatApiService extends AiChatApi {
    private readonly aiApiUrl = injectWidgetConfigProp('aiApiUrl');
    private readonly dataSourceUrl = injectWidgetConfigProp('dataSourceUrl');
    private readonly context = injectWidgetConfigProp('context');
    private readonly interceptorsService = inject(AiChatInterceptorsService);
    private readonly serverApiService = inject(AiChatRequestService);
    private readonly routeService = inject(AiChatRouteService);
    private readonly injector = inject(Injector);
    private readonly contextContributors: AiRequestContextContributor[] = inject(AI_REQUEST_CONTEXT_CONTRIBUTORS, { optional: true }) || [];

    private readonly interruptSignal$ = new Subject<void>();

    get sourceLink(): string {
        return this.dataSourceUrl;
    }

    public initThread(): Observable<InitThreadResponse> {
        const body: AiInitThreadDescriptor['body'] = {
            context: this.buildRequestContext()
        };

        const response = this.request<AiInitThreadDescriptor, InitThreadResponse>({
            method: 'POST',
            endpoint: 'init_thread',
            body
        }).pipe(map((res) => res ?? { thread_id: null }));

        return this.interceptorsService.runInitThreadResponseInterceptors(response);
    }

    public getSuggestions(): Observable<SuggestionsResponse> {
        const body: AiSuggestionsDescriptor['body'] = {
            url: this.requestContext.url
        };
        const response = this.request<AiSuggestionsDescriptor, SuggestionsResponse>({
            method: 'POST',
            endpoint: 'suggestions',
            body
        }).pipe(filter(Boolean));
        return this.interceptorsService.runSuggestionsResponseInterceptors(response);
    }

    public restoreThread(threadId: string): Observable<RestoreThreadResponse> {
        return this.request<AiRestoreThreadDescriptor, RestoreThreadResponse>({
            method: 'POST',
            endpoint: `restore_thread/${threadId}`
        });
    }

    public prompt(threadId: string | null, userMessage: string, contextExtras?: Record<string, unknown>): Observable<PromptResponse> {
        const rawUserMessage = of(userMessage);
        return this.interceptorsService.runBeforeRequest(rawUserMessage).pipe(
            switchMap((userMessageAfterInterceptors) => this.sendPrompt(threadId, userMessageAfterInterceptors, contextExtras)),
            switchMap(({ key }) =>
                this.pullLlmResponse(key).pipe(takeUntil(this.interruptSignal$.pipe(switchMap(() => this.sendInterruptRequest(key)))))
            ),
            distinctUntilChanged((prev, curr) => isSameUserInteractMessage(prev.response, curr.response)),
            switchMap(({ isDone, response }) =>
                this.interceptorsService.runStreamingInterceptors(of(response)).pipe(
                    switchMap((r) => (isDone ? this.interceptorsService.runAfterResponse(of(r)) : of(r)))
                )
            )
        );
    }

    private pullLlmResponse(key: string): Observable<PollResult> {
        return this.getLLmResponse(key).pipe(
            expand((response) => (response.isDone ? EMPTY : this.getLLmResponse(key))),
            takeWhile((response) => !response.isDone, true),
            map(this.parseMessage),
            filter(this.isValidCommandResponse),
            map((response) => ({ isDone: response.isDone, response: { commands: response.message.commands } }))
        );
    }

    private parseMessage({ message, isDone }: RawStreamResponse): StreamResponse | null {
        if (message.trim() !== '') {
            try {
                return { message: JSON.parse(message), isDone };
            } catch {
                return null;
            }
        }
        return null;
    }

    private isValidCommandResponse(response: StreamResponse | null): response is StreamResponse {
        const msg = response?.message;
        return typeof msg === 'object' && msg !== null && Array.isArray(msg.commands) && msg.commands.length > 0;
    }

    private sendPrompt(threadId: string | null, message: string, contextExtras?: Record<string, unknown>): Observable<{ key: string }> {
        const body: AiPromptDescriptor['body'] = {
            user_message: message,
            context: this.buildRequestContext(contextExtras),
            thread_id: threadId
        };
        return this.request<AiPromptDescriptor, { key: string }>({ method: 'POST', endpoint: `start_fetch_llm_response`, body });
    }

    /** Base context, then each contributor (later wins per key), then this prompt's extras. */
    private buildRequestContext(extras?: Record<string, unknown>): AiRequestContext {
        const contributed = this.contextContributors.reduce<Record<string, unknown>>((acc, contribute) => {
            try {
                return { ...acc, ...dropUndefined(contribute()) };
            } catch {
                return acc;
            }
        }, {});
        return { ...this.requestContext, ...contributed, ...dropUndefined(extras ?? {}) } as AiRequestContext;
    }

    private get requestContext(): AiRequestContext {
        const { applicationArea, urlGetter } = this.context;

        return {
            applicationArea,
            url: urlGetter(),
            route: this.routeService.currentRoute,
            ui_view_context: this.resolveUiViewContext()
        };
    }

    private resolveUiViewContext(): unknown {
        const getter = this.context.uiViewContextGetter;
        if (!getter) {
            return null;
        }
        try {
            return runInInjectionContext(this.injector, getter) ?? null;
        } catch {
            return null;
        }
    }

    private getLLmResponse(key: string): Observable<RawStreamResponse> {
        return this.request<AiStreamDescriptor, RawStreamResponse>({
            method: 'POST',
            endpoint: 'llm_response',
            body: key
        });
    }

    public sendFeedback(threadId: string, messageId: string, userFeedback: UserFeedback): Observable<void> {
        const body: AiSendFeedbackDescriptor['body'] = {
            threadId,
            messageId,
            userFeedback
        };

        return this.request<AiSendFeedbackDescriptor, void>({
            method: 'POST',
            endpoint: 'user_feedback',
            body
        });
    }

    public interrupt(): void {
        this.interruptSignal$.next();
    }

    private sendInterruptRequest(key: string): Observable<InterruptResponse> {
        const body: InterruptRequest = { sessionId: key };

        return this.request<AiInterruptDescriptor, InterruptResponse>({
            method: 'POST',
            endpoint: 'interrupt',
            body
        });
    }

    private request<Descriptor extends AiRequestDescriptor<any, any, any>, Response extends AiResponseType>({
        method,
        endpoint,
        body
    }: Descriptor): Observable<Response> {
        const context = new HttpContext();
        const url = `${this.aiApiUrl}/${endpoint}`;
        const request = this.serverApiService.request<Response>({ method, url, options: { body, context } });

        if (ngDevMode) {
            return request.pipe(
                catchError((error: unknown) => {
                    console.error(`AI request failed:`, error);

                    return throwError(() => error);
                })
            );
        }
        return request;
    }
}

function dropUndefined(record: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined));
}

function isSameUserInteractMessage(prev: PromptResponse, curr: PromptResponse): boolean {
    const previousCommand = prev.commands[0];
    const currentCommand = curr.commands[0];
    return (
        previousCommand.type === 'user_interact' &&
        currentCommand.type === 'user_interact' &&
        previousCommand.message === currentCommand.message &&
        previousCommand.messageId === currentCommand.messageId
    );
}
